<template>
  <div class="wecarexm-ai-container">
    <!-- Background Effects -->
    <div class="background-effects">
      <div class="gradient-orb orb-1" />
      <div class="gradient-orb orb-2" />
      <div class="gradient-orb orb-3" />
      <div class="grid-pattern" />
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Logo -->
      <div class="ai-header">
        <VImg
          :width="300"
          src="/logo/light.svg"
        />
      </div>

      <!-- Transcription Card -->
      <div class="transcription-card">
        <!-- Card Body -->
        <div class="card-body">
          <!-- Tickets Display -->
          <div
            v-if="showTickets"
            class="tickets-display"
          >
            <div class="tickets-header">
              <VIcon
                icon="tabler-sparkles"
                size="22"
                color="primary"
              />
              <span class="tickets-title">Generated Tickets</span>
            </div>

            <div class="tickets-list">
              <div
                v-for="(ticket, index) in generatedTickets"
                :key="index"
                class="ticket-item"
              >
                <div class="ticket-top-row">
                  <VChip
                    :color="getTypeColor(ticket.type)"
                    size="small"
                    variant="tonal"
                  >
                    {{ ticket.type }}
                  </VChip>
                  <VChip
                    :color="getPriorityColor(ticket.priority)"
                    size="small"
                    variant="tonal"
                  >
                    {{ ticket.priority }}
                  </VChip>
                </div>

                <div class="ticket-details">
                  <div class="ticket-row">
                    <span class="ticket-label">Department</span>
                    <span class="ticket-value">{{ ticket.department }}</span>
                  </div>
                  <div class="ticket-row">
                    <span class="ticket-label">Category</span>
                    <span class="ticket-value">{{ ticket.category }}</span>
                  </div>
                  <div class="ticket-row">
                    <span class="ticket-label">Sub Category</span>
                    <span class="ticket-value">{{ ticket.sub_category }}</span>
                  </div>
                </div>

                <p class="ticket-description">
                  {{ ticket.description }}
                </p>
              </div>
            </div>
          </div>

          <!-- Idle / Connecting State -->
          <div
            v-else-if="isIdle || isConnecting"
            class="idle-content"
          >
            <p class="idle-text">
              Speak To See Live Translation
            </p>

            <!-- Start Recording inline with content -->
            <button
              v-if="isIdle"
              class="start-btn"
              @click="handleStartRecording"
            >
              <VIcon
                icon="tabler-microphone"
                size="20"
              />
              <span>Start Recording</span>
            </button>
            <button
              v-else
              class="start-btn connecting"
              disabled
            >
              <VProgressCircular
                size="20"
                width="2"
                indeterminate
              />
              <span>Connecting...</span>
            </button>
          </div>

          <!-- Recording / Completed: Transcription Display -->
          <div
            v-else
            ref="transcriptionContainer"
            class="transcription-display"
          >
            <div class="transcription-inner">
              <p
                v-for="(segment, index) in transcriptionSegments"
                :key="index"
                class="segment-text"
                :class="{ 'segment-latest': isRecording && index === latestSegmentIndex }"
              >
                {{ segment }}
              </p>

              <!-- Listening indicator when no text yet -->
              <p
                v-if="isRecording && transcriptionSegments.length === 0"
                class="listening-text"
              >
                Listening...
              </p>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <Transition name="fade">
          <div
            v-if="error"
            class="error-message"
          >
            <VIcon
              icon="tabler-alert-circle"
              size="16"
              color="error"
            />
            <span>{{ error }}</span>
          </div>
        </Transition>

        <!-- Recording Bar (absolutely positioned at bottom) -->
        <div
          v-if="isRecording"
          class="recording-bar-wrapper"
        >
          <div class="recording-bar">
            <span class="recording-timer">{{ formattedDuration }}</span>
            <div class="waveform-area">
              <canvas
                ref="waveformCanvas"
                :width="180"
                :height="24"
                class="waveform-canvas"
              />
            </div>
            <button
              class="stop-btn"
              @click="handleStopRecording"
            >
              <div class="stop-icon" />
            </button>
          </div>
        </div>

        <!-- Completed / Tickets Footer -->
        <div
          v-if="isCompleted || showTickets"
          class="card-footer"
        >
          <div class="bottom-row">
            <button
              class="record-again-btn"
              @click="handleReset"
            >
              <VIcon
                icon="tabler-microphone"
                size="20"
                color="white"
              />
              <span>Record Again</span>
            </button>
            <button
              v-if="!showTickets"
              class="generate-ticket-btn"
              :disabled="isGeneratingTicket"
              @click="handleGenerateTicket"
            >
              <VProgressCircular
                v-if="isGeneratingTicket"
                size="20"
                width="2"
                indeterminate
                color="white"
              />
              <VIcon
                v-else
                icon="tabler-ticket"
                size="20"
                color="white"
              />
              <span>{{ isGeneratingTicket ? 'Generating...' : 'Generate Ticket' }}</span>
            </button>
          </div>
        </div>

        <!-- Gradient wash at bottom of card -->
        <div class="card-wash" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useSarvamStreaming } from '@/composables/useSarvamStreaming'

definePage({
  meta: {
    layout: 'blank',
    public: true,
    unauthenticated: true,
  },
})

const waveformCanvas = ref(null)
const transcriptionContainer = ref(null)
const isGeneratingTicket = ref(false)
const generatedTickets = ref([])
const showTickets = ref(false)
let waveformAnimationId = null

const {
  isIdle,
  isConnecting,
  isRecording,
  isCompleted,
  transcriptionSegments,
  latestSegmentIndex,
  formattedDuration,
  error,
  fullTranscription,
  startRecording,
  stopRecording,
  reset,
  getAnalyserData,
} = useSarvamStreaming()

const handleStartRecording = () => {
  startRecording()
}

const handleStopRecording = () => {
  stopWaveformAnimation()
  stopRecording()
}

const handleReset = () => {
  generatedTickets.value = []
  showTickets.value = false
  reset()
}

const handleGenerateTicket = async () => {
  if (isGeneratingTicket.value || !fullTranscription.value) return

  isGeneratingTicket.value = true

  try {
    const { data } = await $api('/wecarexm-ai/ticket-generation', {
      method: 'POST',
      body: {
        feedback: fullTranscription.value,
      },
    })

    generatedTickets.value = data || []
    showTickets.value = true
  } catch (err) {
    console.error('Ticket generation failed:', err)
  } finally {
    isGeneratingTicket.value = false
  }
}

const getPriorityColor = priority => {
  const colors = { high: 'error', medium: 'warning', low: 'success' }

  return colors[priority?.toLowerCase()] || 'info'
}

const getTypeColor = type => {
  const colors = { complaint: 'error', feedback: 'info', suggestion: 'success', appreciation: 'primary' }

  return colors[type?.toLowerCase()] || 'info'
}

// Waveform canvas animation
function startWaveformAnimation() {
  drawWaveform()
}

function stopWaveformAnimation() {
  if (waveformAnimationId) {
    cancelAnimationFrame(waveformAnimationId)
    waveformAnimationId = null
  }
}

function drawWaveform() {
  const canvas = waveformCanvas.value
  if (!canvas || !isRecording.value) {
    waveformAnimationId = null

    return
  }

  const ctx = canvas.getContext('2d')
  const width = canvas.width
  const height = canvas.height

  ctx.clearRect(0, 0, width, height)

  const data = getAnalyserData()
  const barCount = 28
  const barWidth = 2.5
  const totalWidth = barCount * barWidth
  const gap = (width - totalWidth) / (barCount - 1)

  for (let i = 0; i < barCount; i++) {
    let barHeight = 4

    if (data && data.length > 0) {
      const dataIndex = Math.floor((i / barCount) * data.length)
      const sample = data[dataIndex] || 128
      const amplitude = Math.abs(sample - 128) / 128

      barHeight = Math.max(4, amplitude * (height - 4) + 4)
    }

    // Subtle animation for liveliness
    barHeight += Math.sin(Date.now() * 0.008 + i * 0.4) * 2
    barHeight = Math.max(3, Math.min(barHeight, height - 2))

    const x = i * (barWidth + gap)
    const y = (height - barHeight) / 2

    ctx.fillStyle = 'rgba(99, 102, 241, 0.5)'
    ctx.beginPath()
    if (ctx.roundRect) {
      ctx.roundRect(x, y, barWidth, barHeight, 1)
    } else {
      ctx.rect(x, y, barWidth, barHeight)
    }
    ctx.fill()
  }

  waveformAnimationId = requestAnimationFrame(drawWaveform)
}

watch(isRecording, recording => {
  if (recording) {
    nextTick(() => startWaveformAnimation())
  } else {
    stopWaveformAnimation()
  }
})

// Auto-scroll so the last sentence stays centered
watch(() => transcriptionSegments.value.length, () => {
  nextTick(() => {
    const el = transcriptionContainer.value
    if (!el) return

    const lastSegment = el.querySelector('.segment-text:last-of-type')
    if (lastSegment) {
      lastSegment.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  })
})

onUnmounted(() => {
  stopWaveformAnimation()
})
</script>

<style lang="scss" scoped>
.wecarexm-ai-container {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #eef2ff 0%, #f0f4ff 50%, #e8ecf8 100%);
  overflow: hidden;
}

// Background Effects
.background-effects {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.4;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(147, 130, 255, 0.25) 0%, transparent 70%);
  top: -10%;
  left: -10%;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(99, 179, 237, 0.2) 0%, transparent 70%);
  bottom: -15%;
  right: -15%;
  animation-delay: -7s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(129, 140, 248, 0.2) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

.grid-pattern {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(147, 130, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(147, 130, 255, 0.04) 1px, transparent 1px);
  background-size: 50px 50px;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
}

// Main Content
.main-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 2rem 1rem;
}

// Header
.ai-header {
  display: flex;
  justify-content: center;
  padding: 1rem 0 1.5rem;
}

// Transcription Card
.transcription-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  height: 65vh;
  background: #fff;
  border-radius: 28px;
  border: 1.5px solid rgba(165, 155, 220, 0.35);
  outline: 6px solid rgba(165, 155, 220, 0.12);
  outline-offset: -1px;
  overflow: hidden;

}

.card-body {
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 2.5rem 2rem;
  position: relative;
  z-index: 1;
  overflow: hidden;
  min-height: 0;
}

// Idle Content
.idle-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  text-align: center;
}

.idle-text {
  font-size: 1.625rem;
  font-weight: 400;
  color: #1a1a2e;
  line-height: 1.4;
  margin: 0;
}

// Transcription Display
.transcription-display {
  width: 100%;
  text-align: center;
  overflow-y: auto;
  mask-image: linear-gradient(to bottom, transparent 0%, black 30%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 30%);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.transcription-inner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
  padding-top: 4rem;
  padding-bottom: 100%;
}

.segment-text {
  font-size: 1.25rem;
  font-weight: 400;
  color: #1a1a2e;
  line-height: 1.6;
  margin: 0 0 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.segment-latest {
  color: rgb(var(--v-theme-primary));
}

.listening-text {
  font-size: 1.125rem;
  color: rgba(0, 0, 0, 0.35);
  margin: 0;
  animation: pulse-opacity 2s ease-in-out infinite;
}

@keyframes pulse-opacity {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

// Tickets Display
.tickets-display {
  width: 100%;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tickets-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.tickets-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a2e;
}

.tickets-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ticket-item {
  background: rgba(var(--v-theme-primary), 0.04);
  border: 1px solid rgba(var(--v-theme-primary), 0.12);
  border-radius: 16px;
  padding: 1rem 1.25rem;
}

.ticket-top-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.ticket-details {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
}

.ticket-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.ticket-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  min-width: 100px;
  flex-shrink: 0;
}

.ticket-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a2e;
  text-transform: capitalize;
}

.ticket-description {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
  margin: 0;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

// Error Message
.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 2rem;
  position: relative;
  z-index: 1;

  span {
    font-size: 0.8125rem;
    color: rgb(var(--v-theme-error));
  }
}

// Card Footer
.card-footer {
  padding: 1.5rem 2rem 2rem;
  position: relative;
  z-index: 1;
}

// Start Recording Button
.start-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  border-radius: 50px;
  background: transparent;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #1a1a2e;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(0, 0, 0, 0.3);
    background: rgba(0, 0, 0, 0.03);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &.connecting {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Recording Bar
.recording-bar-wrapper {
  position: absolute;
  bottom: 1.25rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2;
  padding: 0 1.5rem;
}

.recording-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 50px;
  width: 100%;
  max-width: 320px;
}

.recording-timer {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6366f1;
  min-width: 40px;
  flex-shrink: 0;
}

.waveform-area {
  flex: 1;
  height: 24px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.waveform-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.stop-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(0, 0, 0, 0.3);
    background: rgba(0, 0, 0, 0.03);
  }

  &:active {
    transform: scale(0.95);
  }
}

.stop-icon {
  width: 12px;
  height: 12px;
  background: #ef4444;
  border-radius: 2px;
}

// Bottom Row (Record Again + Generate Ticket)
.bottom-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
}

.record-again-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  border: none;
  border-radius: 50px;
  background: #1a1a2e;
  color: #fff;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #2d2d44;
  }

  &:active {
    transform: scale(0.97);
  }
}

.generate-ticket-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  border: none;
  border-radius: 50px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// Card gradient wash at bottom
.card-wash {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to top, rgba(185, 195, 240, 0.45) 0%, rgba(195, 205, 245, 0.15) 50%, transparent 100%);
  border-radius: 0 0 28px 28px;
  pointer-events: none;
  z-index: 0;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Responsive
@media (max-width: 520px) {
  .main-content {
    padding: 1rem 0.75rem;
  }

  .transcription-card {
    min-height: 70vh;
    border-radius: 24px;
  }

  .card-body {
    padding: 2rem 1.5rem;
  }

  .card-footer {
    padding: 1.25rem 1.5rem 1.75rem;
  }

  .idle-text {
    font-size: 1.375rem;
  }

  .segment-text {
    font-size: 1.125rem;
  }

  .card-wash {
    border-radius: 0 0 24px 24px;
  }
}
</style>
